import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { validate } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../store/hooks'

import {
  selectWebAppStateCompanyUserId,
  selectWebAppStateMySelf,
  selectWebAppStateMySelfHasAnalyticsAccess,
} from '../../store/getters/web-app.getters'
import { setShowSnackBar } from '../../store/reducers/web-app.reducer'
import { buildRelativePath, PathConfig } from '../../core/config/PathConfig'
import {
  analyticDetailsV2StateNewConstellationGetAuthorRecoV3Action,
  analyticDetailsV2StateNewConstellationGetBookRecoV3Action,
  analyticDetailsV2StatePostNewAnalyticsAction,
  analyticDetailsV2StatePutAnalyticDetailsByIdAction,
  analyticDetailsV2StateSetClilNameFilterAction,
  analyticsDetailV2StateGetMockReadershipProfile,
  analyticsDetailV2StateGetReportByIdAction,
  analyticsDetailV2StateNewAnalyticAction,
} from '../../store/actions/analytics-details/analytic-details-v2.actions'
import AnalyticDetailsLayout from '../../components/analytics-details-v2/AnalyticDetailsLayout'
import {
  selectAnalyticDetailsStateAnalyticDetails,
  selectAnalyticDetailsStateAnalyticDetailsAuthorsInput,
  selectAnalyticDetailsStateAnalyticDetailsAuthorsOutput,
  selectAnalyticDetailsStateAnalyticDetailsBooksInput,
  selectAnalyticDetailsStateAnalyticDetailsBooksOutput,
  selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
  selectAnalyticDetailsStateIsLoadingPostNewAnalytic,
  selectAnalyticDetailsStateIsLoadingPutAnalytic,
  selectAnalyticDetailsStateNeedSaveAnalyticDetails,
  selectAnalyticDetailsStatePostNewAnalyticError,
  selectAnalyticDetailsStatePostNewAnalyticSuccess,
  selectAnalyticDetailsStatePutAnalyticError,
  selectAnalyticDetailsStatePutAnalyticSuccess,
} from '../../store/getters/analytics-details/analytic-details-v2.getters'
import {
  analyticsDetailV2StateSetAnalyticDetailsName,
  resetPostNewAnalyticStatus,
  resetPutAnalyticStatus,
} from '../../store/reducers/analytics-details/analytic-details-v2.reducer'
import { AnalyticsTypeEnum } from '../../core/enums/analytics-type.enum'
import SwitchViewAnalytic from '../../components/analytics-details-v2/view/SwitchViewAnalytic'
import GlDialog from '../../components/share/dialog/GlDialog'
import GlSpinner from '../../components/share/GlSpinner'
import GlMatInputOutline from '../../components/share/inputs/GlMatInputOutline'
import {
  selectNewAnalyticsReadershipProfileStatePostPreviewError,
  selectNewAnalyticsReadershipProfileStatePostPreviewTypeError,
} from '../../store/getters/analytics-details/new-analytics-readership-profile.getters'
import { AnalyticPreviewTypeErrorEnum } from '../../core/enums/analytic-preview-type-error'
import BackGroundPreviewTypeError from '../../assets/images/preview-report-type-error.svg'
import AnalyticsDashboardShareDialog from '../../components/analytics-dashboard/more-actions/share-analytics/AnalyticsDashboardShareDialog'
import { patchReportService } from '../../core/api/services/bookmetrie/reports.service'
import { AnalyticsStatusEnum } from '../../core/enums/analytics-status.enum'
import { AnalyticsPermissionEnum } from '../../core/enums/analytics-permission.enum'
import GlDialogBody from '../../components/share/dialog/GlDialogBody'
import GlDialogTitle from '../../components/share/dialog/GlDialogTitle'
import GLDialogButtonStyle from '../../components/share/dialog/GLDialogButtonStyle'
import AnalyticsDialogExit from '../../components/share/dialog/AnalyticsDialogExit'

const AnalyticDetailsView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [renameAnalyticDialog, setRenameAnalyticDialog] =
    useState<boolean>(false)
  const [hasNavigatePrevious, setHasNavigatePrevious] = useState<boolean>(false)
  const [firstEanSearch, setFirstEanSearch] = useState<string | null>('')
  const [shareDialog, setShareDialog] = useState<boolean>(false)
  const [saveBeforeExitDialog, setSaveBeforeExitDialog] =
    useState<boolean>(false)
  const [displayPreviewErrorDialog, setDisplayPreviewErrorDialog] =
    useState<boolean>(true)
  const [renameInputValue, setRenameInputValue] = useState<string>('')

  const { reportId: urlReportId } = useParams<{ reportId: string }>()
  const [reportId, setReportId] = useState<string>(urlReportId as string)
  const [searchParams] = useSearchParams()

  const dispatch = useAppDispatch()

  const isLoadingGetAnalyticDetails = useAppSelector(
    selectAnalyticDetailsStateIsLoadingGetAnalyticDetails,
  )

  const companyUserId = useAppSelector(selectWebAppStateCompanyUserId)

  const isLoadingPostNewAnalytic = useAppSelector(
    selectAnalyticDetailsStateIsLoadingPostNewAnalytic,
  )

  const isLoadingPutAnalytic = useAppSelector(
    selectAnalyticDetailsStateIsLoadingPutAnalytic,
  )
  const analyticDetails = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetails,
  )

  const mySelf = useAppSelector(selectWebAppStateMySelf)
  const hasAnalyticsAccess = useAppSelector(
    selectWebAppStateMySelfHasAnalyticsAccess,
  )
  useEffect(() => {
    if (mySelf && !hasAnalyticsAccess) {
      dispatch(setShowSnackBar(t('webApp.notAccessToFeature')))
      navigate(buildRelativePath([PathConfig.BOOKMETRIE]))
    }
  }, [mySelf, hasAnalyticsAccess])

  const booksInput =
    useAppSelector(selectAnalyticDetailsStateAnalyticDetailsBooksInput) || []
  const booksOutput = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsBooksOutput,
  )

  const authorsInput =
    useAppSelector(selectAnalyticDetailsStateAnalyticDetailsAuthorsInput) || []
  const authorsOutput = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetailsAuthorsOutput,
  )

  function manageNavigatePrevious() {
    if (!firstEanSearch) {
      if (reportId === 'new') {
        setFirstEanSearch(searchParams.get('eans'))
      } else if (analyticDetails?.inputs[0]?.values) {
        setFirstEanSearch(analyticDetails.inputs[0].values[0])
      }
    } else if (firstEanSearch !== searchParams.get('eans')) {
      setHasNavigatePrevious(!!searchParams.get('eans'))
    } else {
      setHasNavigatePrevious(false)
    }

    if (!searchParams.get('eans')) {
      setFirstEanSearch(null)
    }
  }

  useEffect(() => {
    setReportId(urlReportId as string)
  }, [urlReportId])

  // Au chargement de la page
  useEffect(() => {
    if (hasAnalyticsAccess) {
      const type = searchParams.get('type')
      const eans = searchParams.get('eans')
      const authors = searchParams.get('authors')
      const clilFilter = searchParams.get('popular_catalog_clil_name_filter')
      if (reportId === 'new' && type) {
        const analyticType = type as AnalyticsTypeEnum
        dispatch(analyticsDetailV2StateNewAnalyticAction({ analyticType }))
          .then(() => {
            if (
              analyticType === AnalyticsTypeEnum.WORK_MIRROR ||
              analyticType === AnalyticsTypeEnum.PRIVATE_WORK_MIRROR
            ) {
              if (eans) {
                const eanIds = `${eans}`
                  .split(',')
                  .map((ean: string) => Number(ean))

                if (clilFilter && clilFilter !== '') {
                  void dispatch(
                    analyticDetailsV2StateSetClilNameFilterAction({
                      clilNameFilter: clilFilter,
                    }),
                  )
                    .then(() => {
                      void dispatch(
                        analyticDetailsV2StateNewConstellationGetBookRecoV3Action(
                          {
                            eanIds,
                          },
                        ),
                      )
                    })
                    .catch(() => {})
                } else {
                  void dispatch(
                    analyticDetailsV2StateNewConstellationGetBookRecoV3Action({
                      eanIds,
                    }),
                  )
                }
              }
            } else if (
              analyticType === AnalyticsTypeEnum.AUTHOR_MIRROR ||
              analyticType === AnalyticsTypeEnum.PRIVATE_AUTHOR_MIRROR
            ) {
              if (authors) {
                const authorIds = `${authors}`.split(',')
                void dispatch(
                  analyticDetailsV2StateNewConstellationGetAuthorRecoV3Action({
                    authorIds,
                  }),
                )
              }
            }
          })
          .catch(() => {})
          .finally(() => {})
      }

      if (
        validate(reportId) &&
        !type &&
        !eans &&
        !authors &&
        (!analyticDetails || analyticDetails.id !== reportId)
      ) {
        void dispatch(analyticsDetailV2StateGetReportByIdAction({ reportId }))
      }

      if (reportId === 'readership_profile') {
        void dispatch(analyticsDetailV2StateGetMockReadershipProfile())
      }

      manageNavigatePrevious()
    }
  }, [
    reportId,
    searchParams.get('type'),
    searchParams.get('eans'),
    searchParams.get('authors'),
    searchParams.get('popular_catalog_clil_name_filter'),
  ])

  const postAnalytic = (toExit: boolean) => {
    if (
      (booksInput.length && booksOutput.length) ||
      (authorsInput.length && authorsOutput.length)
    ) {
      dispatch(analyticDetailsV2StatePostNewAnalyticsAction())
        .then(() => {
          if (toExit) {
            navigate(
              buildRelativePath([PathConfig.BOOKMETRIE, PathConfig.ANALYTICS]),
            )
          }
          setSaveBeforeExitDialog(false)
          setRenameAnalyticDialog(false)
        })
        .finally(() => dispatch(resetPostNewAnalyticStatus()))
    }
  }

  const putAnalytic = (toExit: boolean) => {
    dispatch(analyticDetailsV2StatePutAnalyticDetailsByIdAction())
      .unwrap()
      .then(() => {
        if (toExit) {
          navigate(
            buildRelativePath([PathConfig.BOOKMETRIE, PathConfig.ANALYTICS]),
          )
        }
        setSaveBeforeExitDialog(false)
        setRenameAnalyticDialog(false)
      })
      .finally(() => dispatch(resetPutAnalyticStatus()))
  }

  const postAnalyticSuccess = useAppSelector(
    selectAnalyticDetailsStatePostNewAnalyticSuccess,
  )
  const postAnalyticError = useAppSelector(
    selectAnalyticDetailsStatePostNewAnalyticError,
  )
  const putAnalyticSuccess = useAppSelector(
    selectAnalyticDetailsStatePutAnalyticSuccess,
  )
  const putAnalyticError = useAppSelector(
    selectAnalyticDetailsStatePutAnalyticError,
  )

  const needSaveAnalyticDetails = useAppSelector(
    selectAnalyticDetailsStateNeedSaveAnalyticDetails,
  )

  const postPreviewError = useAppSelector(
    selectNewAnalyticsReadershipProfileStatePostPreviewError,
  )
  const postPreviewTypeError = useAppSelector(
    selectNewAnalyticsReadershipProfileStatePostPreviewTypeError,
  )

  useEffect(() => {
    if (postPreviewError && !displayPreviewErrorDialog) {
      setDisplayPreviewErrorDialog(true)
    }
  }, [postPreviewError])

  useEffect(() => {
    if ((putAnalyticSuccess || postAnalyticSuccess) && analyticDetails?.id) {
      void dispatch(
        setShowSnackBar(
          t('snackbarMessage.saveReportConfirm', {
            reportName: analyticDetails?.name || '',
          }),
        ),
      )
    }
    if (postAnalyticSuccess && analyticDetails?.id) {
      navigate(
        buildRelativePath([
          PathConfig.BOOKMETRIE,
          PathConfig.ANALYTICS,
          analyticDetails?.id,
        ]),
      )
    }

    if (putAnalyticError || postAnalyticError) {
      dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
    }
  }, [
    analyticDetails,
    postAnalyticSuccess,
    postAnalyticError,
    putAnalyticError,
    putAnalyticSuccess,
    reportId,
  ])

  useEffect(() => {
    setRenameInputValue(analyticDetails?.name || '')
  }, [analyticDetails?.name])

  const handleChangeAnalyticName = (name: string) => {
    if (name.trim()) {
      dispatch(analyticsDetailV2StateSetAnalyticDetailsName(name.trim()))
    } else {
      setRenameInputValue(analyticDetails?.name || '')
    }
  }

  const handleSaveAnalytics = () => {
    setRenameAnalyticDialog(true)
  }
  const handleSaveAnalyticMain = (toExit = false) => {
    if (reportId === 'new') {
      void postAnalytic(toExit)
    } else {
      void putAnalytic(toExit)
    }
  }

  const handleRenameAnalytic = (newName: string) => {
    if (newName === analyticDetails.name) return
    patchReportService(analyticDetails.id, { name: newName })
      .then(() => {
        setRenameInputValue(newName)
        dispatch(
          setShowSnackBar(
            t('snackbarMessage.renameReportConfirm', {
              reportName: newName,
            }),
          ),
        )
      })
      .catch(() => {
        dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
      })
  }

  const onValidateExitDialog = useCallback(() => {
    if (saveBeforeExitDialog) {
      navigate(buildRelativePath([PathConfig.BOOKMETRIE, PathConfig.ANALYTICS]))
    }
    setSaveBeforeExitDialog(false)
  }, [saveBeforeExitDialog])

  const onCloseExitDialog = useCallback(() => {
    setSaveBeforeExitDialog(false)
  }, [])

  const goBackToDashboard = useCallback(() => {
    let path

    switch (analyticDetails.type) {
      case AnalyticsTypeEnum.PRIVATE_AUTHOR_MIRROR:
      case AnalyticsTypeEnum.PRIVATE_WORK_MIRROR:
        path = buildRelativePath([
          PathConfig.BOOKMETRIE,
          PathConfig.PRIVATE_REPORTS,
        ])
        break
      default:
        path = buildRelativePath([PathConfig.BOOKMETRIE, PathConfig.ANALYTICS])
        break
    }

    return navigate(path)
  }, [analyticDetails?.type, navigate])

  return (
    <>
      <AnalyticDetailsLayout
        handleRenameAnalytic={handleRenameAnalytic}
        analyticName={analyticDetails?.name || ''}
        sharedBy={[
          analyticDetails?.shared_by?.first_name,
          analyticDetails?.shared_by?.last_name,
        ]
          .join(' ')
          .trim()}
        disableEdit={
          analyticDetails?.status !== AnalyticsStatusEnum.GENERATED ||
          analyticDetails.permission === AnalyticsPermissionEnum.READ
        }
        hasButtonPrevious={hasNavigatePrevious}
        isLoading={isLoadingGetAnalyticDetails}
        handleExitAnalyticDetails={() => {
          if (needSaveAnalyticDetails) {
            setSaveBeforeExitDialog(true)
          } else {
            goBackToDashboard()
          }
        }}
      >
        <SwitchViewAnalytic
          type={analyticDetails?.type}
          handleSaveAnalytics={handleSaveAnalytics}
          handleShareAnalytics={() => setShareDialog(true)}
        />
      </AnalyticDetailsLayout>
      {postPreviewError && (
        <GlDialog
          open={displayPreviewErrorDialog}
          onClose={() => {
            setDisplayPreviewErrorDialog(false)
          }}
          backgroundImage={BackGroundPreviewTypeError}
        >
          <div
            className="flex flex-col gap-2 justify-end	"
            style={{
              width: 580,
              height: 312,
            }}
          >
            <GlDialogTitle
              label={
                (postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.SUBJECT_TOO_SMALL &&
                  t('NotEnoughDataOnSubject')) ||
                (postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.COMPARISON_TOO_SMALL &&
                  t('NotEnoughDataOnComparison')) ||
                ''
              }
            />
            <GlDialogBody color="#252525">
              <div>
                {postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.SUBJECT_TOO_SMALL &&
                  t('SubjectTooSmall')}
                {postPreviewTypeError ===
                  AnalyticPreviewTypeErrorEnum.COMPARISON_TOO_SMALL &&
                  t('ComparisonTooSmall')}
              </div>
              <div>{t('MayChangeSearch')}</div>
            </GlDialogBody>
            <div className="flex flex-row justify-between items-center gap-4">
              <GLDialogButtonStyle
                type="button"
                className="relative bg-[#252525] flex-1 border-[#252525] mt-6 hover:opacity-60 active:opacity-100"
                primary
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault()
                  setDisplayPreviewErrorDialog(false)
                }}
              >
                <span className="text-[#ffffff] font-medium">
                  {t('commons.buttons.close')}
                </span>
              </GLDialogButtonStyle>
            </div>
          </div>
        </GlDialog>
      )}
      {(analyticDetails?.type === AnalyticsTypeEnum.READERSHIP_PROFILE && (
        <AnalyticsDialogExit
          open={saveBeforeExitDialog}
          onValidate={onValidateExitDialog}
          onClose={onCloseExitDialog}
        />
      )) || (
        <GlDialog
          open={renameAnalyticDialog || saveBeforeExitDialog}
          onClose={() => {
            setRenameAnalyticDialog(false)
            setSaveBeforeExitDialog(false)
          }}
        >
          <div
            className="flex flex-col gap-4"
            style={{
              width: 432,
            }}
          >
            <GlDialogTitle label={t('toSave')} />
            <GlDialogBody textAlignLeft color="#252525">
              {saveBeforeExitDialog && (
                <div>{t('wishSaveSearchBeforeToExit')}</div>
              )}
              <GlMatInputOutline
                type="text"
                onChangeValue={(value: string | number) =>
                  setRenameInputValue(value as string)
                }
                label=""
                value={renameInputValue}
                placeholder={t('analyticNamePlaceHolder')}
                hasClearButton
                hasErrorMessage={t('analyticNameRequired')}
              />
            </GlDialogBody>
            <div className="flex flex-row justify-between items-center gap-4">
              <GLDialogButtonStyle
                type="button"
                className="hover:bg-[#f7f7f7] active:bg-[#e3e3e3] flex-1 flex flex-row items-center justify-center gap-2"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault()
                  if (saveBeforeExitDialog) {
                    goBackToDashboard()
                  }
                  setRenameInputValue(analyticDetails?.name || '')
                  setRenameAnalyticDialog(false)
                  setSaveBeforeExitDialog(false)
                }}
              >
                {(saveBeforeExitDialog && (
                  <span>{t('commons.buttons.exit')}</span>
                )) || <span>{t('commons.buttons.cancel')}</span>}
              </GLDialogButtonStyle>
              <GLDialogButtonStyle
                type="button"
                className="relative text-[#ffffff] bg-[#252525] flex-1 border-[#252525] hover:opacity-60 active:opacity-100"
                primary
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault()
                  handleChangeAnalyticName(renameInputValue)
                  handleSaveAnalyticMain(saveBeforeExitDialog)
                }}
                isLoading={isLoadingPostNewAnalytic || isLoadingPutAnalytic}
                disabled={!analyticDetails?.name}
              >
                {((isLoadingPostNewAnalytic || isLoadingPutAnalytic) && (
                  <div className="absolute w-full h-full top-0 left-0 justify-center items-center">
                    <GlSpinner />
                  </div>
                )) ||
                  null}
                {t('commons.buttons.save')}
              </GLDialogButtonStyle>
            </div>
          </div>
        </GlDialog>
      )}
      <GlDialog open={shareDialog} onClose={() => setShareDialog(false)}>
        {analyticDetails && (
          <AnalyticsDashboardShareDialog
            analyticsName={analyticDetails?.name}
            analyticsId={analyticDetails?.id}
            owner={analyticDetails?.owner}
            companyUserId={companyUserId}
            slug={analyticDetails?.slug}
            analyticsType={analyticDetails?.type}
          />
        )}
      </GlDialog>
    </>
  )
}
export default AnalyticDetailsView
